import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useFirestore, useUser } from 'reactfire'
import {
  collection,
  getDocs,
  query,
  where,
  type Firestore,
} from 'firebase/firestore'
import { useFeatureFlags } from './useFeatureFlags'
import { FEATURE_FLAGS } from './featureFlagKeys'

interface UserCompanyUidsState {
  companyUids: string[]
  loading: boolean
}

/**
 * Fetch the company UIDs the given user belongs to (as employer or operator).
 * Router- and hook-free so it can also run in `OptionalIntercomProvider`
 * (index.jsx), which mounts outside <BrowserRouter>. Mirrors the employer +
 * operator company queries in `useProfilesSelector` (src/lib/profiles.jsx) so it
 * reuses the same Firestore composite indexes. Throws on a failed query — the
 * callers fail closed (treat the flag as off).
 */
export async function fetchUserCompanyUids(
  firestore: Firestore,
  uid: string
): Promise<string[]> {
  const companies = collection(firestore, 'companies')
  const [employerSnap, operatorSnap] = await Promise.all([
    getDocs(
      query(
        companies,
        where('deleted', '==', false),
        where('active', '==', true),
        where('employers', 'array-contains', uid)
      )
    ),
    getDocs(
      query(
        companies,
        where('deleted', '==', false),
        where('active', '==', true),
        where('operator.userUid', '==', uid)
      )
    ),
  ])
  const uids = new Set<string>()
  employerSnap.forEach((doc) => uids.add(doc.id))
  operatorSnap.forEach((doc) => uids.add(doc.id))
  return [...uids]
}

/**
 * Resolve the set of company UIDs the signed-in user belongs to, used to gate
 * the company-audience `chatwoot_companies_enabled` flag app-wide. Mirrors the employer +
 * operator company queries in `useProfilesSelector` (src/lib/profiles.jsx) so it
 * reuses the same Firestore composite indexes. Custom claims do not carry
 * company memberships, so a query is the only source.
 *
 * Fails open to `loading` until both queries resolve; callers should treat the
 * flag as off while loading.
 */
export function useUserCompanyUids(): UserCompanyUidsState {
  const firestore = useFirestore()
  // `user` is undefined while auth is still resolving, null once signed out
  // (mirrors useAudienceFlag).
  const { data: user } = useUser()

  const [state, setState] = useState<UserCompanyUidsState>({
    companyUids: [],
    loading: true,
  })

  useEffect(() => {
    if (user === undefined) {
      setState({ companyUids: [], loading: true })
      return
    }
    if (user === null) {
      // Signed-out users belong to no company.
      setState({ companyUids: [], loading: false })
      return
    }

    const uid = user.uid
    let cancelled = false
    // Clear stale membership from a previously signed-in user while the new
    // queries run, so the flag never stays enabled for the old user's
    // allowlisted company during the refetch window.
    setState({ companyUids: [], loading: true })
    fetchUserCompanyUids(firestore, uid)
      .then((companyUids) => {
        if (cancelled) return
        setState({ companyUids, loading: false })
      })
      .catch(() => {
        // Fail closed: an unreadable query leaves the flag off rather than
        // exposing Chatwoot on a read error.
        if (cancelled) return
        setState({ companyUids: [], loading: false })
      })

    return () => {
      cancelled = true
    }
  }, [firestore, user])

  return state
}

/**
 * Resolve a company-audience flag for a fixed set of company IDs. Returns the
 * evaluated `enabled` value together with `ready` (has Remote Config finished
 * loading?). `enabled` is computed synchronously during render — NOT deferred to
 * an effect — so it settles in the SAME render as its inputs (`pending`,
 * `companyIds`). That matters for callers that also expose a loading gate: a
 * deferred effect would leave a one-render window of `enabled: false` after
 * `pending` flips false, which a consumer booting a competing widget would
 * misread as a resolved "off". Shared by the per-user and per-company hooks.
 */
export function useCompanyAudienceFlag(
  key: string,
  companyIds: readonly string[],
  pending: boolean
): { enabled: boolean; ready: boolean } {
  const featureFlags = useFeatureFlags()
  // Subscribe to route changes so a dev `?<flag>=...` query override re-evaluates.
  useLocation()
  const [ready, setReady] = useState(() => featureFlags.isReady())

  useEffect(() => {
    if (ready) {
      return
    }
    let isMounted = true
    void featureFlags.ready.finally(() => {
      if (isMounted) {
        setReady(true)
      }
    })
    return () => {
      isMounted = false
    }
  }, [featureFlags, ready])

  const enabled =
    !pending && ready
      ? featureFlags.getCompanyAudienceFlag(key, companyIds)
      : false
  return { enabled, ready }
}

export interface ChatwootUserGateState {
  enabled: boolean
  /**
   * True until BOTH the user's company memberships and Remote Config have
   * settled. Consumers that boot a competing widget (the Intercom launcher) MUST
   * hold off booting while this is true: `enabled` reads `false` until then, and
   * booting Intercom in that window leaves an allowlisted user with BOTH launchers
   * — the injected Intercom launcher can't be cleanly torn down afterwards (see
   * the OptionalIntercomProvider note in index.jsx). `enabled` and `loading`
   * settle in the same render, so there is no resolved-but-disabled gap.
   */
  loading: boolean
}

/**
 * App-wide Chatwoot gate for the signed-in user, exposing the membership-loading
 * state. `enabled` is true when `chatwoot_companies_enabled` is globally enabled
 * or the user belongs to ANY allowlisted company. Prefer this over
 * `useChatwootEnabledForUser` wherever the false-vs-not-yet-known distinction
 * matters — i.e. anything that boots Intercom.
 */
export function useChatwootEnabledForUserState(): ChatwootUserGateState {
  const { companyUids, loading: membershipLoading } = useUserCompanyUids()
  const { enabled, ready } = useCompanyAudienceFlag(
    FEATURE_FLAGS.CHATWOOT_COMPANIES_ENABLED,
    companyUids,
    membershipLoading
  )
  // Stay `loading` until BOTH membership AND Remote Config have settled, so the
  // gate never reports a resolved `{ enabled: false, loading: false }` while the
  // real value is still pending — a booter would misread that as "not a Chatwoot
  // user" and start Intercom too early.
  return { enabled, loading: membershipLoading || !ready }
}

/**
 * App-wide Chatwoot gate for the signed-in user: true when `chatwoot_companies_enabled` is
 * globally enabled, or when the user belongs to ANY allowlisted company. Use in
 * shell/global consumers that have no single company in scope (operator shell,
 * admin shell) and do NOT boot a competing widget; otherwise use
 * `useChatwootEnabledForUserState` and gate on `loading`.
 */
export function useChatwootEnabledForUser(): boolean {
  return useChatwootEnabledForUserState().enabled
}

/**
 * Per-company Chatwoot gate: true when `chatwoot_companies_enabled` is globally enabled or
 * the given company is allowlisted. Use where a single company is in scope (the
 * customer bubble, the employer page, an operator viewing one company).
 */
export function useChatwootEnabledForCompany(
  companyUid: string | null | undefined
): boolean {
  const companyIds = useMemo(
    () => (companyUid ? [companyUid] : []),
    [companyUid]
  )
  return useCompanyAudienceFlag(
    FEATURE_FLAGS.CHATWOOT_COMPANIES_ENABLED,
    companyIds,
    false
  ).enabled
}
